/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { XHubChatEvent } from '../models/event/event';
import type { RoomMember } from '../models/room/room-member';

/**
 * The events emitted by RoomMember.
 * - `Membership`: The membership state for this room member e.g. 'join'.
 * - `Typing`: True if the room member is currently typing.
 */
export enum RoomMemberEvent {
  Membership = 'RoomMember.membership', // Membership change (e.g. join, leave, ban, invite)
  Typing = 'RoomMember.typing', // A member's typing state change
}

export type RoomMemberEventHandlerMap = {
  /**
   * Fires whenever any room member's membership state changes.
   * @param event - The matrix event which caused this event to fire.
   * @param member - The member whose RoomMember.membership changed.
   * @param oldMembership - The previous membership state. Null if it's a new member.
   * @example
   * ```
   * matrixClient.on("RoomMember.membership", function(event, member, oldMembership){
   *   var newState = member.membership;
   * });
   * ```
   */
  [RoomMemberEvent.Membership]: (event: XHubChatEvent, member: RoomMember, oldMembership?: string) => void;
  /**
   * Fires whenever any room member's typing state changes.
   * @param event - The matrix event which caused this event to fire.
   * @param member - The member whose RoomMember.typing changed.
   * @example
   * ```
   * matrixClient.on("RoomMember.typing", function(event, member){
   *   var isTyping = member.typing;
   * });
   * ```
   */
  [RoomMemberEvent.Typing]: (event: XHubChatEvent, member: RoomMember) => void;
};
